/**
 * Seletor de empresa/filial do menu superior (JOR4-7365).
 *
 * Recebe as filiais na forma de origem — `{ id, reason }`, que é o
 * jfe_codigo/jfe_razao devolvido por `auth/branches-allowed`. O resto do
 * front ainda circula esses dados renomeados para CAB_CODIGO/CAB_RAZAO, nome
 * herdado do sistema de risco; a conversão fica no login, e o componente novo
 * não a propaga.
 *
 * Isto também aposenta os itens sentinela que o dxSelectBox exigia no meio da
 * lista (código 0 = "Trocar de Empresa/Filial", código -1 = nome da empresa,
 * desabilitado). Eles existiam porque o widget não tinha onde pôr cabeçalho nem
 * ação; o desenho novo tem os dois lugares.
 */

import { Building2, Check, ChevronDown } from "lucide-react";
import { useCallback, useRef, useState } from "react";

import { usarFecharDropdown } from "../usarFecharDropdown";

export interface Filial {
    /** jfe_codigo. */
    id: number;
    /** jfe_razao. */
    reason: string;
}

export interface PropsSeletorEmpresa {
    /** Razão social da empresa logada — é o que o botão exibe, como hoje. */
    empresa: string;
    filiais: Filial[];
    /** Abre a seleção de empresa/filial. A lógica de troca não muda (RN01). */
    aoTrocarEmpresa: () => void;
}

export const SeletorEmpresa = ({
    empresa,
    filiais,
    aoTrocarEmpresa,
}: PropsSeletorEmpresa) => {
    const [aberto, setAberto] = useState(false);
    const container = useRef<HTMLDivElement>(null);

    const fechar = useCallback(() => setAberto(false), []);

    usarFecharDropdown(aberto, container, fechar);

    return (
        <div className="topbar-ds-seletor" ref={container}>
            <button
                type="button"
                className="topbar-ds-seletor-botao"
                onClick={() => setAberto((atual) => !atual)}
                title={empresa}
            >
                <Building2 size={12} />
                <span className="topbar-ds-seletor-nome">{empresa}</span>
                <ChevronDown
                    size={11}
                    className={aberto ? "is-aberto" : undefined}
                />
            </button>

            {aberto && (
                <div className="topbar-ds-dropdown">
                    <button
                        type="button"
                        className="topbar-ds-dropdown-acao"
                        onClick={() => {
                            setAberto(false);
                            aoTrocarEmpresa();
                        }}
                    >
                        Trocar de Empresa/Filial
                    </button>

                    <div className="topbar-ds-dropdown-lista">
                        {filiais.map((filial) => (
                            <div
                                key={filial.id}
                                className="topbar-ds-dropdown-item is-ativo"
                            >
                                <span>{filial.reason}</span>
                                {/*
                                 * Todas marcadas de propósito: a seleção de
                                 * filial no login é múltipla (dxTagBox), então
                                 * não existe "a filial atual" — todas as
                                 * listadas estão em uso.
                                 */}
                                <Check size={12} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
